import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type TopIndividualVoicesView = {
  /** `Largest voice`, with its stance as the badge. */
  kpis: KPIMetric[];
  /** One row per voice, most mentions first, one key per sentiment. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any voice row is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type TopIndividualVoicesCardProps = WidgetBlockProps & {
  view?: TopIndividualVoicesView;
  /** Opens a voice's posts (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19575` — named accounts ranked by their mentions, split by the
 * tone of what each account actually posted.
 *
 * Figma and the prototype rank by reach: a voice matters for how far it
 * carries, not for how often it posts. The data has no reach (no follower or
 * view counts), so this ranks by mentions until it does.
 *
 * Stance is **derived from the period**, never stored on the row. An account is
 * an advocate because most of what it posted was positive, which is a reading of
 * what the filters selected — deselect the positive sentiments and an advocate
 * stops being one, which is the honest behaviour.
 */
export function TopIndividualVoicesCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TopIndividualVoicesCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No named voices in this period"
      {...cardState(status, hasData, onRetry)}
      title="Top individual voices"
      helpText="Ranks individual accounts by the audience their mentions reached and shows the positive, neutral and negative mentions from each account."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
